import { useEffect, useRef, useState } from 'react';
import { supabase } from '@/shared/lib/supabase';
import { topicoFutebolLive } from '@/shared/lib/canaisTela';
import type { FutebolLivedataPitch } from '@/shared/types';

const STALE_MS       = 25_000;
const FALLBACK_POLL_MS = 15_000;

export interface FutebolWssBroadcastState {
  /** Último frame livedata recebido do bridge WSS (via broadcast). */
  pitch:           FutebolLivedataPitch | null;
  /** Bridge enviou ping ou livedata recentemente. */
  bridgeAtivo:     boolean;
  /** Canal Realtime SUBSCRIBED. */
  canalConectado:  boolean;
}

function pitchMaisRecente(
  a: FutebolLivedataPitch | null,
  b: FutebolLivedataPitch | null,
): FutebolLivedataPitch | null {
  if (!a) return b;
  if (!b) return a;
  return a.uts >= b.uts ? a : b;
}

/**
 * Receptor do relay WSS → Supabase Broadcast privado (`futebol_live_{fixtureId}`).
 *
 * O token BSD fica só na Edge Function `futebol-ws-bridge`. A TV (anon) escuta
 * frames livedata em tempo real sem round-trip PostgreSQL.
 *
 * `fallbackPitch` — CDC em futebol_partida_ativa.livedata_pitch (backup).
 */
export function useFutebolWssBroadcast(
  fixtureId:     number | null,
  fallbackPitch: FutebolLivedataPitch | null,
): FutebolWssBroadcastState {
  const [pitch, setPitch]                   = useState<FutebolLivedataPitch | null>(fallbackPitch);
  const [ultimoFrameMs, setUltimoFrameMs]   = useState(0);
  const [canalConectado, setCanalConectado] = useState(false);

  const fallbackRef = useRef(fallbackPitch);
  useEffect(() => { fallbackRef.current = fallbackPitch; });

  // Reset ao trocar de fixture (fallback inicial vem do CDC da partida)
  useEffect(() => {
    setPitch(fallbackRef.current);
    setUltimoFrameMs(0);
  }, [fixtureId]);

  // Mescla fallback CDC quando broadcast ainda não chegou ou está stale
  useEffect(() => {
    if (!fallbackPitch) return;
    setPitch(prev => {
      if (prev && Date.now() - prev.uts < STALE_MS) return prev;
      return pitchMaisRecente(prev, fallbackPitch);
    });
  }, [fallbackPitch]);

  useEffect(() => {
    if (!fixtureId) {
      setCanalConectado(false);
      return;
    }

    const ch = supabase
      .channel(topicoFutebolLive(fixtureId), { config: { private: true, broadcast: { ack: false } } })
      .on('broadcast', { event: 'livedata' }, ({ payload }) => {
        const p = payload as FutebolLivedataPitch;
        if (!p?.situation) return;
        setPitch(prev => pitchMaisRecente(prev, p) ?? p);
        setUltimoFrameMs(Date.now());
      })
      .on('broadcast', { event: 'bridge_ping' }, () => {
        setUltimoFrameMs(Date.now());
      })
      .subscribe((status) => {
        setCanalConectado(status === 'SUBSCRIBED');
      });

    return () => { supabase.removeChannel(ch); };
  }, [fixtureId]);

  // Poll leve do fallback CDC quando bridge parece offline
  useEffect(() => {
    if (!fixtureId) return;

    const id = setInterval(() => {
      const fb = fallbackRef.current;
      if (!fb) return;
      setPitch(prev => {
        const bridgeFresco = prev && Date.now() - prev.uts < STALE_MS;
        if (bridgeFresco) return prev;
        return pitchMaisRecente(prev, fb);
      });
    }, FALLBACK_POLL_MS);

    return () => clearInterval(id);
  }, [fixtureId]);

  const bridgeAtivo = ultimoFrameMs > 0 && Date.now() - ultimoFrameMs < STALE_MS;

  return { pitch, bridgeAtivo, canalConectado };
}
